import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext, { StoredContext } from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys.js';
import { GuardedElement } from './../general-components/authentication';
import AppMenuItem from './../navigation/AppMenuItem';
import styles from './Navigation.module.css';
import logoWhite from '../../images/LogohvidFinal.png';
import logoBlack from '../../images/Logoblack.png';
import logo_white from '../../images/Logo_white_no_money.png';

import {
    Update,
    Map,
    Search,
    Bookmarks,
    Storage,
    ListAlt,
    Assignment,
    CorporateFare,
    LiveTv,
    Settings,
    Logout,
    Verified,
    AccountCircle,
    AddCircle,
    Troubleshoot,
    Article,
    AllInclusive,
    Edit,
    NotificationAdd,
    BarChart,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
    AlignJustify,
    Folder,
    Info,
    FileText,
    Cable,
    Activity,
} from 'lucide-react';
import OrganizationToggle from '../orgnization/OrganizationToggle';
import PERMISSION_KEYS from '../../constants/permission-keys';
import UnreadNotifications from '../notifications/UnreadNotifications';

type NavigationProps = {
    isOpen: boolean;
    toggleMenu: () => void;
};

const Navigation: FC<NavigationProps> = ({ isOpen, toggleMenu }) => {
    const { t } = useTranslation();
    const context = useContext(AuthContext);
    let ctx: StoredContext = context.getContext();

    const [isLoading, setIsLoading] = useState(true);
    const [userInfo, setUserInfo] = useState<any>([]);

    const getUserData = async () => {
        const info = await context.getAllInfo();
        setUserInfo(info);
        setIsLoading(false);
    };

    useEffect(() => {
        getUserData();
    }, []);

    return (
        <GuardedElement auth={ctx.isLoggedIn}>
            <div>
                <div className={styles.hamburger}>
                    <div className="flex bg-[#212121] h-full items-center justify-center">
                        <div onClick={toggleMenu} className="absolute left-4">
                            <AlignJustify strokeWidth={1} />
                        </div>
                        <div className="flex items-center">
                            <div className=" w-44">
                                <a href="/">
                                    <img
                                        src={
                                            context.theme === 'dark'
                                                ? logo_white
                                                : logoBlack
                                        }
                                    />
                                </a>
                            </div>
                        </div>
                        <div className="absolute right-4">
                            <UnreadNotifications variant="darkHeader" />
                        </div>
                    </div>
                </div>
                <nav
                    className={`${styles.wrapper} ${isOpen ? styles.open : ''}`}
                >
                    <div className={styles.logo}>
                        <a href="/">
                            <img
                                src={
                                    context.theme === 'dark'
                                        ? logoWhite
                                        : logoBlack
                                }
                            />
                        </a>
                    </div>
                    <ul className={styles['app-menu']} onClick={toggleMenu}>
                        <AppMenuItem
                            path="/search"
                            isHomeScreen={true}
                            auth={context.orRights([RIGHTS_KEYS.dbSearch])}
                            content={
                                <div>
                                    <Search />
                                    {t('navigation.menuItems.search')}
                                </div>
                            }
                        >
                            <ul className={styles['sub-menu']}>
                                <AppMenuItem
                                    path="/history"
                                    auth={true}
                                    type="sub"
                                    content={t('navigation.menuItems.history')}
                                ></AppMenuItem>
                            </ul>
                        </AppMenuItem>
                        <AppMenuItem
                            path="/all"
                            auth={context.orRights([RIGHTS_KEYS.deptTools])}
                            content={
                                <div>
                                    <AllInclusive />{' '}
                                    {t('navigation.menuItems.allImplants')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/bookmarks"
                            auth={context.orRights([
                                RIGHTS_KEYS.advancedSearch,
                            ])}
                            content={
                                <div>
                                    <Bookmarks />{' '}
                                    {t('navigation.menuItems.bookmarks')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/sync-verification"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Cable /> Sync Verification
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/active-implants"
                            auth={context.orRights([
                                RIGHTS_KEYS.advancedSearch,
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Activity /> Active Implants
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/approved"
                            teaser={ctx.actor?.role === 'PremiumUser'}
                            auth={
                                context.orRights([RIGHTS_KEYS.deptTools]) &&
                                ctx.department !== undefined
                            }
                            content={
                                <div>
                                    <Verified />{' '}
                                    {t('navigation.menuItems.approved')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/implants"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageImplant,
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Storage />{' '}
                                    {t('navigation.menuItems.implants')}
                                </div>
                            }
                        >
                            <div className={styles['sub-menu']}>
                                <ul>
                                    <AppMenuItem
                                        path="/reverification"
                                        auth={context.orRights([
                                            RIGHTS_KEYS.reverifyImplants,
                                        ])}
                                        type="sub"
                                        content={t(
                                            'navigation.menuItems.reverification'
                                        )}
                                    ></AppMenuItem>
                                    <AppMenuItem
                                        path="/list-source-files"
                                        auth={true}
                                        type="sub"
                                        content={t(
                                            'navigation.menuItems.allPDFs'
                                        )}
                                    ></AppMenuItem>
                                </ul>
                            </div>
                        </AppMenuItem>
                        <AppMenuItem
                            path="/add-implant?refresh=true"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageImplant,
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <AddCircle />
                                    {t('navigation.menuItems.addImplant')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/searches"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageImplant,
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Map />
                                    {t('navigation.menuItems.searches')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/search-issues"
                            isHomeScreen={false}
                            auth={context.orRights([
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Troubleshoot />
                                    {t('navigation.menuItems.searchIssue')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/missing-implant-reports"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Edit />
                                    {t(
                                        'navigation.menuItems.missingImplantReports'
                                    )}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/manage-reports"
                            auth={context.orRights([RIGHTS_KEYS.cptReporting])}
                            content={
                                <div>
                                    <FileText />
                                    {t(
                                        'navigation.menuItems.manageReports',
                                        'CPT Reports'
                                    )}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/cpt-analytics"
                            auth={
                                context.orRights([RIGHTS_KEYS.cptReporting]) &&
                                context.hasPermission(
                                    PERMISSION_KEYS.cptAnalytics
                                )
                            }
                            content={
                                <div>
                                    <BarChart />
                                    CPT Analytics
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/recent-implants"
                            auth={context.orRights([
                                RIGHTS_KEYS.verifyImplants,
                            ])}
                            content={
                                <div>
                                    <Update />
                                    {t('navigation.menuItems.recentImplants')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/predefined/manage"
                            auth={context.orRights([
                                RIGHTS_KEYS.managePredefined,
                            ])}
                            content={
                                <div>
                                    <ListAlt /> {t('navigation.menuItems.wc')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/room-overview"
                            auth={
                                context.orRights([
                                    RIGHTS_KEYS.viewMRIOverview,
                                ]) && ctx.department?.name !== undefined
                            }
                            content={
                                <div>
                                    <Assignment />{' '}
                                    {t('navigation.menuItems.roomOverview')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/facility"
                            auth={
                                context.orRights([
                                    RIGHTS_KEYS.manageEmployees,
                                    RIGHTS_KEYS.manageDepartmentInfo,
                                ]) && ctx.department?.name !== undefined
                            }
                            content={
                                <div>
                                    <CorporateFare />{' '}
                                    {t('navigation.menuItems.facilityOverview')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/metrics"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageEmployees,
                            ])}
                            content={
                                <div>
                                    <BarChart />{' '}
                                    {t('navigation.menuItems.metrics')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/admin/facilities"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageDepartments,
                                RIGHTS_KEYS.manageAllUsers,
                            ])}
                            content={
                                <div>
                                    <CorporateFare />{' '}
                                    {t('navigation.menuItems.facilities')}
                                </div>
                            }
                        />

                        {/* <AppMenuItem
                            path="/protocols"
                            auth={context.orRights([RIGHTS_KEYS.deptTools])}
                            teaser={ctx.actor?.role === 'PremiumUser'}
                            content={
                                <div>
                                    <Article /> Protocols
                                </div>
                            }
                        >
                            {' '}
                            {ctx.deptPermissions?.includes(
                                'Manage Protocols'
                            ) && (
                                <div className={styles['sub-menu']}>
                                    <ul>
                                        <AppMenuItem
                                            path="/protocols/manage"
                                            type="sub"
                                            auth={true}
                                            content={
                                                <div>Manage Protocols</div>
                                            }
                                        ></AppMenuItem>
                                    </ul>
                                </div>
                            )}
                        </AppMenuItem> */}

                        <AppMenuItem
                            path="/department-policies"
                            auth={
                                context.orRights([RIGHTS_KEYS.deptTools]) &&
                                (context.orRights([RIGHTS_KEYS.viewPolicies]) ||
                                    context.hasPermission(
                                        PERMISSION_KEYS.managePolicies
                                    ))
                            }
                            content={
                                <div>
                                    <Settings />{' '}
                                    {t(
                                        'navigation.menuItems.departmentPolicies'
                                    )}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/files"
                            auth={context.orRights([
                                RIGHTS_KEYS.viewDepartmentFiles,
                                RIGHTS_KEYS.manageAllImplants,
                            ])}
                            content={
                                <div>
                                    <Folder /> {t('navigation.menuItems.files')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/video-overview"
                            auth={context.orRights([RIGHTS_KEYS.deptTools])}
                            content={
                                <div>
                                    <LiveTv />{' '}
                                    {t('navigation.menuItems.videos')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/guide"
                            auth={context.orRights([RIGHTS_KEYS.deptTools])}
                            content={
                                <div>
                                    <Info />{' '}
                                    {t('navigation.menuItems.resourceCenter')}
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/manage-notifications"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageNotifications,
                            ])}
                            content={
                                <div>
                                    <NotificationAdd /> Manage Notifications
                                </div>
                            }
                        ></AppMenuItem>
                        <AppMenuItem
                            path="/admin"
                            auth={context.orRights([
                                RIGHTS_KEYS.manageActors,
                                RIGHTS_KEYS.manageAllImplants,
                                RIGHTS_KEYS.manageDocumentSchemas,
                                RIGHTS_KEYS.manageAllUsers,
                                RIGHTS_KEYS.manageEndUsers,
                            ])}
                            content={
                                <div>
                                    <Settings />{' '}
                                    {t('navigation.menuItems.settings')}
                                </div>
                            }
                        ></AppMenuItem>
                    </ul>
                    <div className={styles.userMenu} onClick={toggleMenu}>
                        {ctx.hasGuestAccess && <OrganizationToggle />}
                        {isLoading && <></>}
                        {!isLoading && (
                            <Link to="/user-profile">
                                <div className={styles.userLink}>
                                    <AccountCircle />
                                    {userInfo.user.name !== ''
                                        ? userInfo.user.name
                                        : t('navigation.userMenu.yourProfile')}
                                </div>
                            </Link>
                        )}
                        <button
                            className={styles.logout}
                            onClick={context.onLogout}
                        >
                            <Logout />
                            {t('navigation.userMenu.logout')}
                        </button>
                    </div>
                </nav>
            </div>
        </GuardedElement>
    );
};

export default Navigation;
